/**
 * WriterProfile — public /writers/:writerId page. Bio, avatar,
 * follower count, Social Capital chip, and every story the writer
 * has published.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, MapPin, Calendar, Hand, Headphones, Clock } from "lucide-react";
import FollowWriterButton from "@/components/publisher/FollowWriterButton";
import SocialCapitalChip from "@/components/publisher/SocialCapitalChip";
import VerifiedBadge from "@/components/publisher/VerifiedBadge";
import ListenChip from "@/components/publisher/ListenChip";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long" }); }
  catch { return ""; }
}

function Avatar({ url, name, size = 96 }) {
  if (url) {
    return (
      <img src={url} alt={name || ""} className="rounded-full object-cover"
           style={{ width: size, height: size }} loading="lazy" />
    );
  }
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return (
    <div className="rounded-full bg-teal-100 text-teal-900 font-bold flex items-center justify-center"
         style={{ width: size, height: size, fontSize: Math.round(size * 0.36), fontFamily: "'Lora', serif" }}>
      {initials || "SP"}
    </div>
  );
}

export default function WriterProfile() {
  const { writerId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${BASE}/writers/${writerId}`);
      if (!r.ok) throw new Error(r.status === 404 ? "Writer not found" : `HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [writerId]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8f5ef" }}>
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2" style={{ background: "#f8f5ef" }}>
        <p className="text-slate-600">{error || "Writer not found."}</p>
        <Link to="/blog" className="text-teal-700 hover:underline text-sm">← Back to Blog</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#f8f5ef" }} data-testid="writer-profile">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Link to="/blog?site=blog" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-6">
          <ArrowLeft className="w-3 h-3" /> Back to SocialPulse Blog
        </Link>

        <header className="grid md:grid-cols-[auto,1fr,auto] gap-6 items-start mb-10">
          <Avatar url={data.avatar_url} name={data.display_name} size={112} />
          <div>
            <h1 className="text-4xl font-bold text-slate-900 inline-flex items-center gap-2" style={{ fontFamily: "'Lora', serif" }} data-testid="writer-profile-name">
              {data.display_name || "SocialPulse Writer"}
              <VerifiedBadge verified={data.verified} size="lg" testId="writer-profile-verified" />
            </h1>
            {data.handle && <div className="text-sm text-slate-500 mt-0.5">@{data.handle}</div>}
            {data.bio && <p className="mt-3 text-slate-700 leading-relaxed max-w-xl">{data.bio}</p>}
            <div className="mt-3 flex items-center gap-4 flex-wrap text-xs text-slate-500">
              {(data.city || data.country) && (
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[data.city, data.country].filter(Boolean).join(", ")}</span>
              )}
              {data.joined_at && (
                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Joined {fmtDate(data.joined_at)}</span>
              )}
              <span data-testid="writer-profile-followers">{data.follower_count.toLocaleString()} follower{data.follower_count === 1 ? "" : "s"}</span>
              <span data-testid="writer-profile-articles">{data.article_count.toLocaleString()} publication{data.article_count === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <FollowWriterButton writerId={writerId} size="md" testIdPrefix="writer-profile-follow" />
          </div>
        </header>

        <div className="grid md:grid-cols-[1fr,240px] gap-6">
          <section data-testid="writer-profile-articles-list">
            <h2 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-4">
              Published stories
            </h2>
            {data.articles.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No published stories yet.</p>
            ) : (
              <div className="space-y-4">
                {data.articles.map((a) => (
                  <Link key={a.id} to={`/blog/${a.slug}`} className="block rounded-2xl bg-white border border-slate-200 p-4 hover:border-teal-400 transition-colors" data-testid={`writer-profile-article-${a.slug}`}>
                    <div className="flex gap-4">
                      {a.cover_image_url && (
                        <img src={a.cover_image_url} alt="" className="w-24 h-24 object-cover rounded-lg flex-none" loading="lazy" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg leading-snug text-slate-900" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>
                          {a.title}
                        </h3>
                        {a.summary && <p className="mt-1 text-sm text-slate-600 line-clamp-2">{a.summary}</p>}
                        <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] text-slate-500">
                          {a.published_at && <span>{fmtDate(a.published_at)}</span>}
                          {a.read_time_min > 0 && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{a.read_time_min} min</span>}
                          <span className="inline-flex items-center gap-1"><Hand className="w-3 h-3" />{a.total_claps.toLocaleString()}</span>
                          <span className="inline-flex items-center gap-1"><Headphones className="w-3 h-3" />{a.tts_play_count.toLocaleString()}</span>
                          <ListenChip slug={a.slug} testIdPrefix={`writer-profile-listen-${a.slug}`} />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          <aside className="space-y-4">
            <SocialCapitalChip capital={data.social_capital} variant="full" testId="writer-profile-capital" />
            <div className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">On SocialPulse</div>
              <div className="text-sm text-slate-700 leading-relaxed">
                {data.display_name}'s work lives at the intersection of place and topic — every story travels via the discovery graph.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
